import { memo, useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage } from '@/_components';
import { DeleteButton, SortableList } from '@/_containers';
import { nameResolver } from '@/_shared';

import type { ContactQueryLinkerSchema } from '../../schemas';

type ClientContactItemProps = {
  item: ContactQueryLinkerSchema;
  removeItemHandler: (item: ContactQueryLinkerSchema) => void;
  isEditable?: boolean;
  className?: string;
  showPrimaryContactAction?: boolean;
  isPrimary?: boolean;
  hasPrimary?: boolean;
  sortable?: boolean;
  setContactType?: (item: ContactQueryLinkerSchema, isPrimary: boolean) => void;
};

function ItemBase({
  item,
  isEditable = false,
  removeItemHandler,
  className,
  sortable = false,
}: ClientContactItemProps) {
  const BaseItem = useMemo(
    () => (
      <section
        className={twMerge(
          'border-c-border-muted text-accent flex min-h-11 flex-1 flex-col flex-nowrap items-start justify-between gap-1.5 border-b py-2 sm:flex-row dark:text-white',
          className
        )}
      >
        <div className="flex w-full items-center gap-1.5">
          <Link
            to="/contacts/$id"
            params={{ id: `${item.id}` }}
            state={{ naturalBack: true }}
            className="group flex items-center gap-2"
          >
            <Avatar>
              <AvatarImage src={item.profile_image?.[0]?.url} />
              <AvatarFallback name={nameResolver(item.first_name, item.last_name)} />
            </Avatar>

            <span className="decoration-primary text-sm decoration-2 group-hover:underline">
              {item.first_name} {item.last_name}
            </span>
          </Link>
        </div>

        {isEditable && (
          <div>
            <DeleteButton
              type="icon"
              size="sm"
              title="Remove Contact?"
              text="The Contact record will not be deleted; only its association will be removed."
              yesButton="Yes, Remove"
              aria-label="Remove Contact"
              callback={() => removeItemHandler(item)}
            />
          </div>
        )}
      </section>
    ),
    [className, isEditable, item, removeItemHandler]
  );

  return sortable ? (
    <SortableList.Item key={item.id} id={item.id} className="items-center!">
      {isEditable && <SortableList.DragHandle className="mr-1! mb-0.5!" />}
      {BaseItem}
    </SortableList.Item>
  ) : (
    BaseItem
  );
}

export const Item = memo(ItemBase);
